import { i18n } from '@/i18n';
import moment from 'moment';
import {
  getFormattedDate,
  getFormattedDateAndTime,
  getServerTime,
} from '../util/dateHelper';
import {
  COUNT_DOWN_TYPE,
  IDisplayCouponDetail,
  COUPON_QRY_TYPE,
  IDisplayCouponRecord,
  COUPON_TYPE,
  CUSTOM_BUTTON_TYPE,
  TRANS_STATE_TYPE,
  useDisplayCoupon,
  getMarkUseWhitelist, BARCODE_TYPE } from '../api/displayCoupon/display-coupon';
import { user } from '@tencent/ppd-base-libs';
import { whitelistUin } from '../api/displayCoupon/notStartWhitelist';

// eslint-disable-next-line @typescript-eslint/no-require-imports
const { barcode, svg2url, qrcode } = require('pure-svg-code');

export const PAGE_SIZE = 10;

let couponList = [] as IDisplayCouponRecord[];
export const countDowningCouponStore = {
  set: (list: IDisplayCouponRecord[]) => {
    couponList = list;
  },
  get: () => couponList,
};

const INIT_PAGE_CONFIG = {
  pageNum: 0,
  qryType: '1',
  pageSize: PAGE_SIZE,
};
let pageConfig = INIT_PAGE_CONFIG;
export const pageConfigStore = {
  reset: (qryType) => {
    pageConfig = { ...INIT_PAGE_CONFIG, qryType };
  },
  set: (config) => {
    pageConfig = { ...pageConfig, ...config };
  },
  get: () => pageConfig,
};

export const getQryType = queryTypeRoute => (queryTypeRoute === COUPON_QRY_TYPE.INVALID
  ? COUPON_QRY_TYPE.INVALID
  : COUPON_QRY_TYPE.VALID); // 默认查询可用券

export const formatCdKey = (cdkey = '') => {
  const MAX_LENGTH = 40;
  if (cdkey.length < MAX_LENGTH) {
    return cdkey;
  }
  return `${cdkey.substr(0, MAX_LENGTH)}...`;
};

export const formattedDiffTime = (diffTime: number) => {
  if (diffTime <= 0) {
    return '';
  }
  const minute = moment.duration(diffTime).minutes();
  const second = moment.duration(diffTime).seconds();
  const format = val => (val >= 10 ? val : `0${val}`);
  return `${format(minute)}:${format(second)}`;
};

export const getBarCode = (cdkey = '', codeType: 'barcode' | 'qrcode', options?: {barcodeType: BARCODE_TYPE}) => {
  let svgStr = '';
  if (codeType === 'barcode') {
    // 目前默认code128, 也支持ean13
    const barcodeType = options?.barcodeType || BARCODE_TYPE.code128;
    svgStr = barcode(cdkey, barcodeType, {
      width: 275,
      barHeight: 50,
    });
  } else {
    svgStr = qrcode({
      content: cdkey,
      padding: 0,
      width: 320,
      height: 320,
      color: '#000000',
      background: '#ffffff',
      ecl: 'L',
    });
  }
  return svg2url(svgStr);
};

export const isCdkeyCoupon = (couponType: COUPON_TYPE) => [
  COUPON_TYPE.CDKEY,
  COUPON_TYPE.SINGLE_CDKEY,
].includes(couponType);

export const isDisplayCoupon = (couponType: COUPON_TYPE) => [
  COUPON_TYPE.CDKEY,
  COUPON_TYPE.SINGLE_CDKEY,
  COUPON_TYPE.NO_CDKEY,
].includes(couponType);

export const isNoCdkeyCoupon = (couponType: COUPON_TYPE) => couponType === COUPON_TYPE.NO_CDKEY;

// eslint-disable-next-line max-len
export const isNeedCountdown = (couponDetail: IDisplayCouponDetail) => couponDetail.countDownType === COUNT_DOWN_TYPE.ENABLE
  || (couponDetail.countDownType === COUNT_DOWN_TYPE.OLD
    && couponDetail.couponType === COUPON_TYPE.NO_CDKEY);

export const isNotStart = (couponDetail: IDisplayCouponDetail) => {
  const uin = user.getUin() || '';
  if (whitelistUin.find(item => uin.includes(item))) {
    // 白名单用户允许尚未开始查看
    return false;
  }

  const currentDate = getServerTime();
  return moment(currentDate).isBefore(moment(couponDetail.validTime));
};

// 不能用!isUsed判断未使用，因为还有可能已过期
export const isUsed = (couponDetail: IDisplayCouponDetail) => couponDetail.transState === TRANS_STATE_TYPE.USED;

export const isExpired = (couponDetail: IDisplayCouponDetail) => {
  const currentDate = getServerTime();
  return !moment(currentDate).isSameOrBefore(moment(couponDetail.expireTime));
};

// eslint-disable-next-line arrow-body-style
export const isShowPage1BtnTopText = (couponDetail: IDisplayCouponDetail) => {
  return (
    !isNotStart(couponDetail)
    && !isExpired(couponDetail)
    && !isUsed(couponDetail)
    && couponDetail.customBtnType === CUSTOM_BUTTON_TYPE.PAYMENT
  );
};

export const getPage1BtnTopText = (couponDetail: IDisplayCouponDetail) => {
  if (couponDetail.customBtnType === CUSTOM_BUTTON_TYPE.PAYMENT) {
    return i18n('該優惠需到店向店員出示使用，不會在支付中自動扣減。');
  }
  return `${i18n('該券適用于')}${couponDetail.couponRange}`;
};

// eslint-disable-next-line arrow-body-style
export const isPage1BtnInvalid = (couponDetail: IDisplayCouponDetail) => {
  return (
    isNotStart(couponDetail) || isUsed(couponDetail) || isExpired(couponDetail)
  );
};

export const getPage1BtnText = (couponDetail: IDisplayCouponDetail) => {
  if (isNotStart(couponDetail)) {
    return i18n('尚未開始');
  }
  if (isUsed(couponDetail)) {
    return i18n('已使用');
  }
  if (isExpired(couponDetail)) {
    return i18n('已過期');
  }
  return i18n('使用本次優惠');
};

// eslint-disable-next-line arrow-body-style
// eslint-disable-next-line max-len
export const isShowPage1BtnBottomText = (couponDetail: IDisplayCouponDetail) => isNotStart(couponDetail) || isUsed(couponDetail);

export const getPage1BtnBottomText = (couponDetail: IDisplayCouponDetail) => {
  if (isNotStart(couponDetail)) {
    return `${getFormattedDate(couponDetail.validTime)} ${i18n('可使用')}`;
  }
  if (isUsed(couponDetail)) {
    return `${i18n('使用該優惠的時間')} ： ${getFormattedDateAndTime(couponDetail.useTime)}`;
  }
};

const getVisitedScids = (): String[] => {
  // get visited coupon scid
  const key = 'discount-center-display-coupon-visited';
  const objStr = localStorage.getItem(key) || '';
  try {
    return JSON.parse(objStr).value;
  } catch (e) {
    return [];
  }
};

export const isVisitedPage2 = (couponDetail: IDisplayCouponDetail): boolean => [
  TRANS_STATE_TYPE.OPENED,
  TRANS_STATE_TYPE.USED,
].includes(couponDetail.transState);

/** 无倒计时标记为已打开 */
export const markVisitedPage2 = async (visitScid: string) => await useDisplayCoupon({
  user_scid: visitScid,
  cmd: 'open',
})
;

// eslint-disable-next-line arrow-body-style
export const isShowStep1Title = (couponDetail: IDisplayCouponDetail) => {
  // 展示步骤一：
  // 1. 只要是cdkey都展示
  // 2. 只要是付款的都展示
  return (
    isCdkeyCoupon(couponDetail.couponType)
    || couponDetail.customBtnType === CUSTOM_BUTTON_TYPE.PAYMENT
  );
};

export const getStep1TitleText = (couponDetail: IDisplayCouponDetail) => {
  if (couponDetail.customBtnType === CUSTOM_BUTTON_TYPE.PAYMENT) {
    return i18n('向店員展示該優惠，並確認優惠已使用');
  }
  if (isCdkeyCoupon(couponDetail.couponType)) {
    return i18n('複製以下優惠碼');
  }
};

// eslint-disable-next-line arrow-body-style
export const isShowCopy = (couponDetail: IDisplayCouponDetail) => {
  return (
    isCdkeyCoupon(couponDetail.couponType)
    && couponDetail.customBtnType !== CUSTOM_BUTTON_TYPE.PAYMENT
  );
};

// eslint-disable-next-line arrow-body-style
export const isShowStep2 = (couponDetail: IDisplayCouponDetail) => {
  // eslint-disable-next-line max-len
  return !(
    couponDetail.couponType === COUPON_TYPE.NO_CDKEY
    && couponDetail.customBtnType !== CUSTOM_BUTTON_TYPE.PAYMENT
  );
};

// eslint-disable-next-line arrow-body-style
export const isShowStep2Btn = (couponDetail: IDisplayCouponDetail) => {
  return couponDetail.customBtnType !== CUSTOM_BUTTON_TYPE.NONE;
};

export const getStep2TopText = (couponDetail: IDisplayCouponDetail) => {
  if (couponDetail.customBtnType === CUSTOM_BUTTON_TYPE.PAYMENT) {
    return i18n('與店員確認已扣減優惠金額，請前往付款');
  }
  return i18n('請前往商家使用優惠');
};

// eslint-disable-next-line arrow-body-style
export const isShowWechatIcon = (couponDetail: IDisplayCouponDetail) => {
  return couponDetail.customBtnType === CUSTOM_BUTTON_TYPE.PAYMENT;
};

export const getStep2BtnText = (couponDetail: IDisplayCouponDetail) => {
  if (couponDetail.customBtnType === CUSTOM_BUTTON_TYPE.CUSTOMIZE) {
    return couponDetail.jumpName;
  }
  if (couponDetail.customBtnType === CUSTOM_BUTTON_TYPE.PAYMENT) {
    return i18n('店員已扣減優惠，前往付款');
  }
};

export const getStep2BtnUrl = (couponDetail: IDisplayCouponDetail) => {
  if (couponDetail.customBtnType === CUSTOM_BUTTON_TYPE.CUSTOMIZE) {
    return couponDetail.jumpUrl;
  }
  if (couponDetail.customBtnType === CUSTOM_BUTTON_TYPE.PAYMENT) {
    const { businessId, jumpPackage } = couponDetail;
    return `https://hkwallet.moneydata.hk/fcgi-bin/app/ewallet_bindcard/ewallet_qrpay_index.fcgi?business_id=${businessId}&jump_package=${jumpPackage}`;
  }
};

// eslint-disable-next-line arrow-body-style
export const getUsageText = (couponDetail: IDisplayCouponDetail) => {
  if (couponDetail.customBtnType === CUSTOM_BUTTON_TYPE.PAYMENT) {
    if (isNeedCountdown(couponDetail)) {
      return `${i18n('1. 點擊「使用本次優惠」並於指定時間內使用')}
      ${i18n('2. 向店員展示優惠券')}
      ${i18n('3. 店員扣減優惠金額後，前往付款')}`;
    }
    return `${i18n('1. 點擊「使用本次優惠」')}
    ${i18n('2. 向店員展示優惠券')}
    ${i18n('3. 店員扣減優惠金額後，前往付款')}`;
  }
  if (isNeedCountdown(couponDetail)) {
    return `${i18n('1. 點擊「使用本次優惠」並於指定時間內使用')}
      ${i18n('2. 前往適用商戶使用優惠')}`;
  }
  return `${i18n('1. 點擊「使用本次優惠」')}
  ${i18n('2. 前往適用商戶使用優惠')}`;
};

export const getConfirmModalText = (couponDetail: IDisplayCouponDetail): { title: string; htmlContent: string } => {
  const htmlContent = i18n('本優惠將於10分鐘後失效，確定使用後自動開啟倒計時，而且不能撤回。要確定使用嗎？');
  if (couponDetail.customBtnType === CUSTOM_BUTTON_TYPE.PAYMENT) {
    return {
      title: i18n('付款前須向店員出示該優惠'),
      htmlContent,
    };
  }
  return {
    title: i18n('請前往商家使用優惠'),
    htmlContent,
  };
};

export const listOnClickCouponTypeInValidPage = (couponInfo: IDisplayCouponRecord) => {
  if (isNotStart(couponInfo as any)) {
    return '1'; // 尚未開始
  } if (isUsed(couponInfo as any)) {
    return '3';// 在可使用列表裡面的已使用券，就是正在倒計時
  }
  return '2'; // 未使用券
};

export const listOnClickCouponTypeInExpirePage = (CouponInfo: IDisplayCouponRecord) => {
  if (isExpired(CouponInfo as any)) {
    return '2'; // 已過期券
  }
  return '1'; // 已使用券
};

export const getPage1PageType = (couponDetail: IDisplayCouponDetail) => {
  if (isNotStart(couponDetail)) {
    return '4';
  }
  if (isUsed(couponDetail)) {
    return '2';
  }
  if (isExpired(couponDetail)) {
    return '3';
  }
  return '1';
};

// 如果在『標記已使用』白名單裡，則返回true
// eslint-disable-next-line arrow-body-style
export const isMarkUseCoupon = (couponDetail: IDisplayCouponDetail) => {
  return getMarkUseWhitelist().includes(couponDetail.couponId);
};

/** 是否跳转小程序 */
export const isLaunchCustomWeApp = (couponDetail: IDisplayCouponDetail): boolean => {
  /** 展示券选择的页面跳转必须是自定义跳转入口 */
  if (couponDetail.customBtnType !== CUSTOM_BUTTON_TYPE.CUSTOMIZE) {
    return false;
  }
  const reg = /https?:\/\/gh_\w+,.+/;
  return reg.test(couponDetail.jumpUrl);
};

/** 从配置的url中提取出第三方小程序的信息 */
export const getWeAppFormHref = (href: string): {
  appId: string,
  appPath: string
} => {
  // https://gh_b5403cc2567a,pages/activity/activity.html?collection_activity_no=7276d42c873be250a1b111a2b813557d
  const appInfo = href.replace(/^https?:\/\//, '');
  const [appId, appPath] = appInfo.split(',');

  return {
    appId,
    appPath,
  };
};
